import React, { useState, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  LinearProgress,
  Alert,
  Card,
  CardContent,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  CheckCircle as SuccessIcon,
  Warning as WarningIcon,
  Description as DocumentIcon,
  FindInPage as ClassifyIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import api from '../shared/services/api';
import { brand, primaryButtonSx } from '../theme';
import { stepperIcons } from '../assets/icons/nav';
import NavIcon from '../shared/components/NavIcon';

interface FileClassificationResult {
  file_name: string;
  is_relevant: boolean;
  label: string;
  confidence: number;
}

interface ClassificationResponse {
  results: FileClassificationResult[];
  total_files: number;
  relevant_count: number;
  not_relevant_count: number;
}

const DocumentClassifier: React.FC = () => {
  const theme = useTheme();
  const [files, setFiles] = useState<File[]>([]);
  const [processing, setProcessing] = useState(false);
  const [results, setResults] = useState<FileClassificationResult[] | null>(null);
  const [summary, setSummary] = useState<{ total: number; relevant: number; notRelevant: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setFiles(prev => [...prev, ...acceptedFiles]);
    setResults(null);
    setSummary(null);
    setError(null);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.tiff', '.bmp'],
      'application/pdf': ['.pdf'],
    },
    multiple: true,
  });

  const handleClassify = async () => {
    if (files.length === 0) return;

    setProcessing(true);
    setError(null);
    setResults(null);
    setSummary(null);

    try {
      const formData = new FormData();
      files.forEach(file => formData.append('files', file));

      const response = await api.post<ClassificationResponse>(
        '/api/v1/doc-classification/classify',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      setResults(response.data.results);
      setSummary({
        total: response.data.total_files,
        relevant: response.data.relevant_count,
        notRelevant: response.data.not_relevant_count,
      });
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Classification failed');
    } finally {
      setProcessing(false);
    }
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleReset = () => {
    setFiles([]);
    setResults(null);
    setSummary(null);
    setError(null);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <Box sx={{ height: '100%', bgcolor: 'grey.50', overflow: 'auto' }}>
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        {/* Page Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
            <ClassifyIcon sx={{ fontSize: 40, color: brand.orange }} />
            <Typography variant="h4" fontWeight={700} sx={{ color: brand.textPrimary }}>
              Document Classifier
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Upload documents to classify whether they are relevant (invoice/budget) or not, using AI-powered image classification.
          </Typography>
        </Box>

        {/* Upload Area */}
        <Paper
          {...getRootProps()}
          elevation={0}
          sx={{
            p: 5,
            mb: 3,
            border: '2px dashed',
            borderColor: isDragActive ? brand.orange : brand.borderSubtle,
            bgcolor: brand.dropZoneBg,
            borderRadius: 1,
            cursor: 'pointer',
            transition: 'all 0.3s',
            textAlign: 'center',
            '&:hover': { borderColor: brand.orange },
          }}
        >
          <input {...getInputProps()} />
          <NavIcon src={stepperIcons.dragDrop} size={48} sx={{ mx: 'auto', mb: 1 }} />
          <Typography variant="body1" gutterBottom sx={{ color: brand.textPrimary, fontWeight: 500 }}>
            {isDragActive ? 'Drop files here...' : 'Drag & drop documents here'}
          </Typography>
          <Typography variant="caption" sx={{ color: brand.textSecondary, display: 'block' }}>
            or click to select files (multiple allowed)
          </Typography>
          <Typography variant="caption" display="block" mt={0.5} sx={{ color: brand.textSecondary }}>
            Supported: PDF, PNG, JPEG, TIFF, BMP (max 30MB each)
          </Typography>
        </Paper>

        {/* Selected Files List */}
        {files.length > 0 && !results && (
          <Box mb={3}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                Files ({files.length})
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  onClick={handleClassify}
                  disabled={processing}
                  startIcon={processing ? undefined : <ClassifyIcon />}
                  sx={primaryButtonSx}
                >
                  {processing ? 'Classifying...' : `Classify ${files.length > 1 ? `All ${files.length} Files` : 'File'}`}
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleReset}
                  disabled={processing}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 1,
                    borderColor: brand.borderSubtle,
                    color: brand.textPrimary,
                    '&:hover': { borderColor: brand.orange, backgroundColor: 'transparent' },
                  }}
                >
                  Clear All
                </Button>
              </Stack>
            </Stack>

            {processing && (
              <Box mb={2}>
                <LinearProgress sx={{ '& .MuiLinearProgress-bar': { bgcolor: brand.orange } }} />
                <Typography variant="caption" color="text.secondary" mt={1}>
                  Analyzing {files.length} document{files.length > 1 ? 's' : ''} with AI model...
                </Typography>
              </Box>
            )}

            <Stack spacing={1}>
              {files.map((file, index) => (
                <Card key={index} variant="outlined">
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <DocumentIcon sx={{ color: brand.orange }} />
                      <Box flex={1}>
                        <Typography variant="body2" fontWeight={600}>{file.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatFileSize(file.size)}
                        </Typography>
                      </Box>
                      <Tooltip title="Remove">
                        <IconButton size="small" onClick={() => removeFile(index)} disabled={processing}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </Box>
        )}

        {/* Error */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Classification Results */}
        {results && summary && (
          <Box mb={3}>
            {/* Summary Banner */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                mb: 3,
                borderRadius: 2,
                bgcolor: brand.dropZoneBg,
                borderLeft: `4px solid ${brand.success}`,
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
                <Box>
                  <Typography variant="h6" fontWeight={700} sx={{ color: brand.textPrimary }}>
                    Classification Complete
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {summary.total} file{summary.total > 1 ? 's' : ''} classified
                  </Typography>
                </Box>
                <Stack direction="row" spacing={2}>
                  <Chip
                    icon={<SuccessIcon />}
                    label={`${summary.relevant} Relevant`}
                    color="success"
                    sx={{ fontWeight: 600, fontSize: '0.85rem', px: 1 }}
                  />
                  <Chip
                    icon={<WarningIcon />}
                    label={`${summary.notRelevant} Not Relevant`}
                    color="warning"
                    sx={{ fontWeight: 600, fontSize: '0.85rem', px: 1 }}
                  />
                </Stack>
              </Stack>
            </Paper>

            {/* Per-File Results */}
            <Stack spacing={1.5}>
              {results.map((result, index) => (
                <Card
                  key={index}
                  variant="outlined"
                  sx={{
                    borderColor: result.is_relevant
                      ? theme.palette.success.light
                      : theme.palette.warning.light,
                    borderWidth: 2,
                  }}
                >
                  <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                    <Stack direction="row" alignItems="center" spacing={2}>
                      {result.is_relevant ? (
                        <SuccessIcon sx={{ color: theme.palette.success.main, fontSize: 28 }} />
                      ) : (
                        <WarningIcon sx={{ color: theme.palette.warning.main, fontSize: 28 }} />
                      )}
                      <Box flex={1}>
                        <Typography variant="body1" fontWeight={600}>
                          {result.file_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Classification: {result.label}
                        </Typography>
                      </Box>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="body2" fontWeight={600} sx={{ minWidth: 50, textAlign: 'right' }}>
                          {(result.confidence * 100).toFixed(1)}%
                        </Typography>
                        <Chip
                          label={result.is_relevant ? 'RELEVANT' : 'NOT RELEVANT'}
                          color={result.is_relevant ? 'success' : 'warning'}
                          size="small"
                          sx={{ fontWeight: 700 }}
                        />
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>

            <Box mt={3}>
              <Button
                variant="outlined"
                onClick={handleReset}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 1,
                  borderColor: brand.borderSubtle,
                  color: brand.textPrimary,
                  '&:hover': { borderColor: brand.orange, backgroundColor: 'transparent' },
                }}
              >
                Classify More Documents
              </Button>
            </Box>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default DocumentClassifier;
